import React from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Unstable_Grid2';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import { useTranslation } from '../../hooks/useTranslatedField';
import { DNGatsbyTemplate } from '../DriveNinjaTemplate/gatsby';
import { graphql } from 'gatsby';

import { createIntl, FormattedMessage, IntlProvider } from 'react-intl';
import ar from './ar.json';
import en from './en.json';
import { ReachAdapter } from 'use-query-params/adapters/reach';
import { LocaleProvider } from '../../../contexts/language';
import queryString from 'query-string';
import {
  BooleanParam,
  NumberParam,
  QueryParamProvider,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import FIELD_CONFIG from '../../../config/fieldConfig';
import { GatsbyImage, getImage, StaticImage } from 'gatsby-plugin-image';
import { useCarLink } from '../../hooks/useCarLink';
import allFields from '../../../config/allFields.json';
import ModelCardFavorite from '../ManufacturerPage/modelCardFavorite';
import useGetSearchParameters from '../../molecules/FacetSection/useGetSearchParameters';
import { filterSearchResults } from '../../../functions/filterSearchResults';
import fieldLookups from '../../../config/fieldLookups.json';
import { searchParamsToLinkFormat } from '../../../functions/searchParamsToLinkFormat';

const languages = {
  ar,
  en
};

const SearchFacets = React.lazy(()=>import('../../molecules/SearchFacets'));
const MobileFilterDialog = React.lazy(()=>import('../../molecules/MobileFilterDialog'));
const MobileFilterFab = React.lazy(()=>import('../../molecules/MobileFilterFab'));
const ActiveFiltersAlert = React.lazy(()=>import( '../../atoms/ActiveFiltersAlert'));

const SearchPage = ({ data, pageContext: { lang, direction, searchData, searchFacets } }) => {
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });
  const l = useLanguageLink();
  const t = useTranslation();
  const cl = useCarLink();
  const searchArgs = {};
  const s = (props) => {
    const qs = queryString.stringify(props);
    return l(`/search/?${qs}`);
  };
  const { searchParams, setSearchParams, activeParams,clearParams } = useGetSearchParameters({ allFields });

  const useGatsbySortedImages = ({ images, name }) => {
    const elements = images?.sort((a, b) => a?.orderingPriority - b?.orderingPriority).map(({ localImage }, idx) =>
      <GatsbyImage
        key={idx}
        image={getImage(localImage)}
        layout="fixed"
        alt={t(name) || ''}
        height={200}
        style={{ width: '100%' }}
      />);
    if (!elements?.length) {
      return [
        <StaticImage
          key={1}
          src={'./images/trimLevelPlaceholder.png'}
          layout="fixed"
          alt={`${name || ''}`}
          height={200}
          style={{ width: '100%' }}
        />
      ];
    }
    return elements;
  };
  const [showMobileFilters, setShowMobileFilters] = useQueryParam('showFilters', BooleanParam);
  const [requestedCurrentPage] = useQueryParam('page', withDefault(NumberParam, 1));
  const [requestedPageSize, setCurrentPage] = useQueryParam('page_size', withDefault(NumberParam, 12));
  const pageSize = (Number.isInteger(parseInt(requestedPageSize))) && parseInt(requestedPageSize) || 12;


  const ranks = {
    IN_STOCK: 1000,
    WAIT_LIST: 2000,
    UNKNOWN: 3000,
    NOT_AVAILABLE: 4000
  };


  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const orderingFunctions = {
    recommended: (a, b) => {
      const a_offers = a.trimLevels.map(tl => tl.offers).reduce((p, c) => [...p, ...c], []);
      const b_offers = b.trimLevels.map(tl => tl.offers).reduce((p, c) => [...p, ...c], []);

      const a_min = Math.min(...a_offers.map(o => parseFloat(o?.monthlyAmount)));
      const b_min = Math.min(...b_offers.map(o => parseFloat(o?.monthlyAmount)));
      const a_stock = Math.min(...a_offers.map(o => ranks[o?.stockAvailability]));
      const b_stock = Math.min(...b_offers.map(o => ranks[o?.stockAvailability]));
      return a_stock- b_stock  ||a_min-b_min;
    },

    price_high: (a, b) => {
      const a_offers = a.trimLevels.map(tl => tl.offers).reduce((p, c) => [...p, ...c], []);
      const b_offers = b.trimLevels.map(tl => tl.offers).reduce((p, c) => [...p, ...c], []);
      const a_min = Math.min(...a_offers.map(o => parseFloat(o?.monthlyAmount)));
      const b_min = Math.min(...b_offers.map(o => parseFloat(o?.monthlyAmount)));
      return b_min - a_min;
    },
    price_low: (a, b) => {
      const a_offers = a.trimLevels.map(tl => tl.offers).reduce((p, c) => [...p, ...c], []);
      const b_offers = b.trimLevels.map(tl => tl.offers).reduce((p, c) => [...p, ...c], []);
      const a_prices = a_offers.map(o => parseFloat(o?.monthlyAmount));
      const b_prices = b_offers.map(o => parseFloat(o?.monthlyAmount));
      const a_min = Math.min(...a_prices);
      const b_min = Math.min(...b_prices);
      const r = a_min - b_min;
      return r;
    }
  };

  const searchResultData = filterSearchResults({ searchParameters: searchParams, searchResults:searchData, allFields, fieldLookups });
  const maxPages = Math.ceil(searchResultData.length / pageSize);



  const humanPageNumber =
    (
      Number.isInteger(parseInt(requestedCurrentPage))
      && parseInt(requestedCurrentPage) <= maxPages
      && parseInt(requestedCurrentPage) > 0
      && parseInt(requestedCurrentPage)
    ) || 1;
  const indexPageNumber = humanPageNumber - 1;
  const start = indexPageNumber * pageSize;
  const end = start + pageSize;

  const displaySearchResults = searchResultData.sort(orderingFunctions[ordering]||orderingFunctions.recommended).slice(start, end);
  const searchParamString=searchParamsToLinkFormat({ searchParameters: searchParams });

  return (
    <LocaleProvider localeConfig={{ lang, direction }}>
      <DNGatsbyTemplate
        pagination={{
          humanPageNumber: humanPageNumber,
          numberOfPages: maxPages,
          getUrl: ({ page }) => s({ ...searchArgs, page })
        }}
        breadcrumbs={[
          {
            name: intl.formatMessage({
              id: 'search.searchBreadcrumb',
              defaultMessage: 'Search'
            }),
            path: l('/search/')
          }
        ]}
      >
        <IntlProvider locale={lang} messages={languages[lang] || languages.en}>
          <Stack spacing={2} sx={{ mb: 3 }}>

            <Grid container spacing={0}>
              <Grid md={4} sx={{
                display: { xs: 'none', lg: 'block' }
              }}>
                <React.Suspense fallback={null}>
                  <SearchFacets
                    fieldConfig={FIELD_CONFIG}
                    facets={searchFacets}
                    searchParameters={searchParams}
                    onChange={setSearchParams}
                  />
                </React.Suspense>
              </Grid>
              <Grid md={12} lg={8}>
                <div>
                  <Grid
                    sx={{ p:2 }}
                    container
                    spacing={3}
                  >
                    <Grid  xs={12} md={8} sx={{ display: 'flex' }}>
                      <Typography
                        variant={'h1'}
                        sx={{ fontSize: '24px', marginTop: 'auto', marginBottom: 'auto' }}
                      >
                        <FormattedMessage id={'searchHeader.numFound'} defaultMessage={'{found} Models Found.'}
                          values={{ found: searchResultData?.length }} />
                      </Typography>
                    </Grid>
                    <Grid  xs={12} md={4} sx={{ width: '100%', display: 'flex' }}>
                      <Select
                        sx={{
                          width: {
                            xs: '100%',
                            md: 'auto'
                          },
                          marginLeft: 'auto'
                        }}
                        value={ordering || 'recommended'}
                        onChange={(e) => {
                          setOrdering(e.target.value);
                          setCurrentPage(undefined);
                        }}
                      >
                        {[
                          {
                            key: 1,
                            label: intl.formatMessage({
                              id: 'searchHeader.ordering.recommended',
                              defaultMessage: 'Sort by Recommended'
                            }),
                            value: 'recommended'
                          },
                          {
                            key: 2,
                            label: intl.formatMessage({
                              id: 'searchHeader.ordering.lowest',
                              defaultMessage: 'Sort by Lowest Price'
                            }),
                            value: 'price_low'
                          },
                          {
                            key: 3,
                            label: intl.formatMessage({
                              id: 'searchHeader.ordering.highest',
                              defaultMessage: 'Sort by Highest Price'
                            }),
                            value: 'price_high'
                          }
                        ].map(({ key, label, value }) => (
                          <MenuItem value={value} key={key}>{label}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                </div>
                <div>
                  <React.Suspense fallback={null}>
                    {activeParams > 0 ? (
                      <ActiveFiltersAlert clearParams={clearParams} activeParams={activeParams}/>
                    ):null}
                  </React.Suspense>


                  <Grid container sx={{ p:2 }} spacing={2}>

                    {displaySearchResults.map((model) => (
                      <Grid
                        sx={{ display: 'flex' }}
                        key={model?.slug}
                        xs={12}
                        sm={6}
                        md={6}
                        // lg={4}
                      ><ModelCardFavorite
                          slug={model.slug}
                          cardProps={{ sx: { width: '100%' } }}
                          images={useGatsbySortedImages({ images: data?.allDriveNinjaModelType?.edges.map(({ node })=>node).filter(({ slug })=>slug===model.slug)?.[0]?.modelImages?.slice(0,8), translatedName: model.name })}
                          title={`${t(model.manufacturerName)} ${t(model.name)}`}
                          link={cl({
                            mfrSlug: model.manufacturer,
                            mdlSlug: model.slug,
                            search:searchParamString
                          })}
                          key={model.slug}
                          linkText={intl.formatMessage(
                            {
                              id: 'manufacturerPage.linkTitle',
                              defaultMessage: 'View model details for {make} {model}'
                            },
                            {
                              make: t(model.manufacturerName),
                              model: t(model.name)
                            }
                          )}
                          stockAvailability={model.trimLevels.map(t=>t.offers).reduce((a,b)=>[...a,...b],[]).sort((a,b)=>ranks[a?.stockAvailability]-ranks[b?.stockAvailability])[0]?.stockAvailability}
                          monthlyPrice={parseFloat(model.trimLevels.map(t=>t.offers).reduce((a,b)=>[...a,...b],[]).sort((a,b)=>a?.monthlyAmount-b?.monthlyAmount)[0]?.monthlyAmount)}
                          purchasePrice={parseFloat(model.trimLevels.map(t=>t.offers).reduce((a,b)=>[...a,...b],[]).sort((a,b)=>a?.purchaseAmount-b?.purchaseAmount)[0]?.purchaseAmount)}
                          currency={model.trimLevels.map(t=>t.offers).reduce((a,b)=>[...a,...b],[]).sort((a,b)=>a?.monthlyAmount-b?.monthlyAmount)[0]?.currency}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </div>
              </Grid>
            </Grid>

            <React.Suspense fallback={null}>
              <MobileFilterFab
                onToggle={()=>setShowMobileFilters(true)}
              />
            </React.Suspense>
          </Stack>
        </IntlProvider>
        <React.Suspense fallback={null}>
          <MobileFilterDialog
            open={showMobileFilters? true: false}
            onClose={()=>setShowMobileFilters(false)}
            fieldConfig={FIELD_CONFIG}
            facets={searchFacets}
            searchParameters={searchParams}
            onChange={setSearchParams}
          />
        </React.Suspense>

      </DNGatsbyTemplate>
    </LocaleProvider>
  );
};


SearchPage.propTypes = {
  pageContext: PropTypes.shape({
    lang: PropTypes.string.isRequired,
    direction: PropTypes.string.isRequired,
    searchData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    searchFacets: PropTypes.shape({}).isRequired
  }),
  data:PropTypes.shape({
    allDriveNinjaModelType:PropTypes.shape({
      edges:PropTypes.arrayOf(PropTypes.shape({
        node:PropTypes.shape({
          slug:PropTypes.string.isRequired,
          modelImages:PropTypes.arrayOf(PropTypes.shape({})).isRequired
        }).isRequired
      })).isRequired
    }).isRequired
  }).isRequired
};

const SearchWrapper = (props) => {
  return (
    <QueryParamProvider adapter={ReachAdapter}
    >
      <SearchPage {...props} />
    </QueryParamProvider>
  );
};
export default SearchWrapper;
export const query = graphql`
  query {
    allDriveNinjaModelType {
      edges{
        node{
          slug
          modelImages {
                orderingPriority
                interiorColor {
                  slug
                }
                exteriorColor {
                  slug
                }
                localImage {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(height: 200, layout: FIXED)
                    id
                  }
                }
              }
        }
      }
    }     
  }
  `;
